import { ExternalProviderKind, OptimizationItem } from "@web-app/types";

const FRIENDLY_FIELD_LABEL: Record<string, string> = {
  "Dropoff Latitude": "Dropoff address",
  "Dropoff Longitude": "Dropoff address",
  "Dropoff Phone Number": "Dropoff phone number",
  "Dropoff Time": "Dropoff time",
  "Manifest Total Value": "Order value",
  "Pickup Latitude": "Pickup address",
  "Pickup Longitude": "Pickup address",
  "Pickup Phone Number": "Pickup phone number",
  "Pickup Time": "Pickup time",
};

const capitalizeFirst = (s: string): string =>
  s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);

const humanizeField = (label: string): string =>
  label.includes(" ") ? label : label.replace(/[._]+/g, " ");

export type ProviderIssue = { detail: string; label: string };

export const splitIssue = (raw: string): ProviderIssue => {
  const separator = raw.indexOf(": ");
  if (separator === -1) {
    return {
      detail: "",
      label: capitalizeFirst(FRIENDLY_FIELD_LABEL[raw] ?? raw),
    };
  }
  const field = raw.slice(0, separator);
  return {
    detail: raw.slice(separator + 2),
    label: capitalizeFirst(FRIENDLY_FIELD_LABEL[field] ?? humanizeField(field)),
  };
};

// Providers disagree about what an order is missing, so the reasons stay
// attributed rather than merged into one list the user cannot act on.
export type ProviderGap = {
  issues: ProviderIssue[];
  kind: ExternalProviderKind;
};

export function itemMissingFieldsByProvider(
  item: OptimizationItem,
): ProviderGap[] {
  const byProvider = new Map<
    ExternalProviderKind,
    Map<string, ProviderIssue>
  >();
  item.candidates.forEach((candidate) => {
    const missing = candidate.missingFields ?? [];
    if (!candidate.providerKind || missing.length === 0) return;
    const kind = candidate.providerKind as ExternalProviderKind;
    const issues = byProvider.get(kind) ?? new Map<string, ProviderIssue>();
    missing.forEach((raw) => {
      const issue = splitIssue(raw);
      issues.set(`${issue.label}|${issue.detail}`, issue);
    });
    byProvider.set(kind, issues);
  });
  return Array.from(byProvider.entries()).map(([kind, issues]) => ({
    issues: Array.from(issues.values()),
    kind,
  }));
}

export const unquotableItems = (
  items: OptimizationItem[],
): OptimizationItem[] =>
  items.filter((item) => itemMissingFieldsByProvider(item).length > 0);
